import { doc, setDoc, getDoc, serverTimestamp, collection, onSnapshot } from 'firebase/firestore';
import { getDocument, setDocument } from '../services/databaseService';

/**
 * Initialize the connection test document in Firestore
 * This document is used to monitor the connection status
 */
export const initializeConnectionTest = async (userId) => {
  if (!userId) {
    console.warn('Cannot initialize connection test: No user ID provided');
    return false;
  }

  // If we're offline, don't even try
  if (!navigator.onLine) {
    console.warn('Cannot initialize connection test: Browser is offline');
    return false;
  }

  try {
    console.log('Initializing connection test document...');

    // Use our proxy service instead of direct Firestore access
    try {
      // Check if document already exists
      const docResult = await getDocument('connectionTests', userId);

      // Create or update the document
      await setDocument('connectionTests', userId, {
        lastUpdated: new Date().toISOString(),
        clientTimestamp: new Date().toISOString(),
        status: 'online'
      });

      console.log('Connection test document initialized successfully');
      return true;
    } catch (innerError) {
      console.error('Error accessing Firestore:', innerError);
      return false;
    }
  } catch (error) {
    console.error('Error initializing connection test document:', error);
    return false;
  }
};

/**
 * Check if Firestore is connected
 * This is a simple check that uses the system status document
 */
export const checkFirestoreConnection = (userId, callback) => {
  try {
    // First try to read the system status document which is publicly readable
    getDocument('system', 'status')
      .then(() => {
        console.log('System status document read successfully');
        callback('connected');
      })
      .catch(error => {
        console.error('Failed to read system status document:', error);

        // If that fails and we have a userId, try the user-specific connection test document
        if (userId) {
          console.log('Trying user-specific connection test document');

          // Try to read the user's connection test document
          getDocument('connectionTests', userId)
            .then(() => {
              console.log('User connection test document read successfully');
              callback('connected');
            })
            .catch(userError => {
              console.error('Failed to read user connection test document:', userError);
              callback('error');
            });
        } else {
          callback('error');
        }
      });

    // Return a no-op cleanup function
    return () => {};
  } catch (error) {
    console.error('Failed to check connection:', error);
    callback('error');
    return () => {};
  }
};

/**
 * Ensure the system status document exists
 */
export const ensureSystemStatusExists = async () => {
  // If we're offline, don't even try
  if (!navigator.onLine) {
    console.warn('Cannot ensure system status: Browser is offline');
    return false;
  }

  try {
    try {
      const docResult = await getDocument('system', 'status');

      if (!docResult.exists) {
        await setDocument('system', 'status', {
          status: 'online',
          lastUpdated: new Date().toISOString(),
          message: 'System is operational'
        });
      }

      return true;
    } catch (innerError) {
      console.error('Error accessing system status document:', innerError);
      return false;
    }
  } catch (error) {
    console.error('Failed to ensure system status document exists:', error);
    return false;
  }
};
